import Checkbox from '@mui/material/Checkbox';
import { useState } from 'react';
import { ControlLabel, FormControl, FormGroup, HelpBlock } from 'react-bootstrap';
import type { ChangeEvent, FormEventHandler, VoidFunctionComponent } from 'react';

import { useDialogContext } from '../../../../../../contexts/DialogContext';
import { window } from '../../../../../../globals';
import Flex from '../../../../../generic/Flex';
import { icons, IconText } from '../../../../../generic/Icon';
import MailToLink from '../../../../../generic/Link/MailToLink';
import Loader from '../../../../../generic/Loader';
import Text from '../../../../../generic/Text';
import Header from '../../../../../shared/settings/SettingsPageHeader';
import useAuthenticationSettingsContext from '../../hooks/useAuthenticationSettingsContext';
import { SSO_DOCS_URL } from '../consts';
import type { LDAPStrategy } from '../../../../../../types';
import type { TestUser } from '../types';

import { defaultLDAPConfig, validationState as validationStateHelper } from './ldapConfigHelpers';
import TestLDAPModal from './TestLDAPModal';

const LDAPAuth: VoidFunctionComponent = () => {
  const [user, setUser] = useState<TestUser>({ username: '', password: '' });
  const [shouldShowTestModal, setShouldShowTestModal] = useState<boolean>(false);
  const { settings, onUpdateLDAP: onUpdateLdap, isLDAPEdited } = useAuthenticationSettingsContext();
  const { onConfirm } = useDialogContext();
  if (!settings) {
    return <Loader />;
  }
  const ldapConfig = settings.ldap;
  const validationState = (ldap: LDAPStrategy | null, field: keyof LDAPStrategy, canSave: boolean) => {
    if (!ldap) {
      return null;
    }
    return validationStateHelper(ldap, field, canSave);
  };
  const onClearConfig = () => {
    onConfirm({
      title: 'Clear LDAP Configuration',
      body: 'This will permanently clear out your LDAP configuration from FOSSA. Users who logged in using LDAP will no longer be able to log in.',
      onConfirm: () => onUpdateLdap(defaultLDAPConfig),
    });
  };

  const updateTestUser = (attr: keyof TestUser) => (ev: ChangeEvent<HTMLInputElement>) => {
    const { value } = ev.target;
    setUser((prevUser) => ({
      ...prevUser,
      [attr]: value,
    }));
  };

  const updateLDAP = (attr: keyof LDAPStrategy) => (ev: ChangeEvent | null, value: string) => {
    const target = ev && (ev.target as HTMLTextAreaElement).value;
    const newValue = target || value;
    onUpdateLdap({
      [attr]: newValue,
      passwordIsSet: attr === 'password' ? false : ldapConfig?.passwordIsSet,
    });
  };

  const toggleCheckbox = (attr: keyof LDAPStrategy) => updateLDAP(attr)(null, !ldapConfig?.[attr]);

  const onHideTestModal = () => {
    setShouldShowTestModal(false);
  };

  if (settings.sso && !settings.sso.ldap) {
    return (
      <Header heading="Single Sign On with LDAP">
        <Header.Paragraph>
          This feature is not included with your current plan. Please contact{' '}
          <MailToLink email="sales@fossa.com">sales</MailToLink> to learn more.
        </Header.Paragraph>
      </Header>
    );
  }

  const canSave = isLDAPEdited;

  return (
    <div className="gray-rounded-box margin-bottom-md sso-settings">
      <Header heading="Single Sign On with LDAP">
        <Header.Button
          type="error"
          size="small"
          onClick={onClearConfig}
        >
          Clear Configuration
        </Header.Button>
        <Header.Button
          type="secondary"
          size="small"
          onClick={() => setShouldShowTestModal(true)}
        >
          Test Connection
        </Header.Button>
        <Header.NavigationButton
          type="secondary"
          size="small"
          to={SSO_DOCS_URL}
        >
          <IconText
            icon={icons.book}
            text="View Documentation"
          />
        </Header.NavigationButton>
        <Header.Paragraph>
          Setting up single sign-on with LDAP will allow users to authenticate against your enterprise user directory.
        </Header.Paragraph>
        <Header.Paragraph>
          FOSSA will authenticate users against your LDAP server using bind authentication.
        </Header.Paragraph>
        <Header.Paragraph>
          When enabled, LDAP will be treated as the preferred login method for users in your organization.
          {!settings.disableInvite &&
            ' Users invited by email will still be able to sign in and manage their password on FOSSA.'}
        </Header.Paragraph>
      </Header>
      <FormGroup style={{ marginBottom: '10px' }}>
        <ControlLabel>Enabled</ControlLabel>
        <Checkbox checked={!!ldapConfig?.enabled} onChange={() => toggleCheckbox('enabled')} />
      </FormGroup>
      <Text.H4>Server Configuration</Text.H4>
      <div className="gray-rounded-box margin-bottom-lg">
        <FormGroup validationState={validationState(ldapConfig, 'server', canSave)}>
          <ControlLabel>Hostname or IP Address</ControlLabel>
          <FormControl
            type="text"
            value={ldapConfig?.server || ''}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={updateLDAP('server') as FormEventHandler<any>}
            placeholder="ldap.domain.com"
          />
          <HelpBlock>The DNS or IP Address of your LDAP server. Do not include the protocol or port.</HelpBlock>
        </FormGroup>
        <Flex align="flex-start" justify="space-between">
          <FormGroup
            style={{ width: '45%', minWidth: '45%', marginBottom: 0 }}
            validationState={validationState(ldapConfig, 'port', canSave)}
          >
            <ControlLabel>Port</ControlLabel>
            <FormControl
              type="number"
              value={ldapConfig?.port || ''}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={updateLDAP('port') as FormEventHandler<any>}
            />
            <HelpBlock>
              The default port when using SSL is 636.
              <br />
              The default port without SSL is 389.
            </HelpBlock>
          </FormGroup>
          <FormGroup
            style={{ width: '48%', minWidth: '48%', marginBottom: 0 }}
            validationState={ldapConfig ? validationState(ldapConfig, 'ssl', canSave) : null}
          >
            <ControlLabel>Use SSL</ControlLabel>
            <Checkbox checked={!!ldapConfig?.ssl} onChange={() => toggleCheckbox('ssl')} />
            <HelpBlock>
              By default, we connect with <code>ldap://</code>.<br />
              When enabled, we&rsquo;ll connect using <code>ldaps://</code>.
            </HelpBlock>
          </FormGroup>
        </Flex>
      </div>
      <Text.H4>Bind Credentials</Text.H4>
      <div className="gray-rounded-box margin-bottom-lg">
        <FormGroup validationState={ldapConfig ? validationState(ldapConfig, 'username', canSave) : null}>
          <ControlLabel>Distinguished Name</ControlLabel>
          <FormControl
            type="text"
            value={ldapConfig?.username || ''}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={updateLDAP('username') as FormEventHandler<any>}
            placeholder="uid=auth@fossa.com,dc=fossa,dc=com"
          />
        </FormGroup>
        <FormGroup validationState={validationState(ldapConfig, 'password', canSave)}>
          <ControlLabel>Secret</ControlLabel>
          <FormControl
            type="password"
            value={ldapConfig?.password || ''}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={updateLDAP('password') as FormEventHandler<any>}
            // If passwordIsSet is true, then the user has already provided a password. The server's response
            // containing the LDAP configuration does not include the password field so that we don't reveal it
            // needlessly, but passwordIsSet indicates whether a password had previously been set.
            placeholder={ldapConfig?.passwordIsSet ? '••••••••' : 'password'}
          />
        </FormGroup>
      </div>
      <Text.H4>User Schema Settings</Text.H4>
      <div className="gray-rounded-box margin-bottom-lg">
        <FormGroup validationState={validationState(ldapConfig, 'searchBase', canSave)}>
          <ControlLabel>User Base Distinguished Name</ControlLabel>
          <FormControl
            type="text"
            value={ldapConfig?.searchBase || ''}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={updateLDAP('searchBase') as FormEventHandler<any>}
            placeholder="dc=fossa,dc=com"
          />
          <HelpBlock>
            The Distinguished Name to use when running queries against your server in order to find a user.
          </HelpBlock>
        </FormGroup>
        <FormGroup validationState={validationState(ldapConfig, 'searchFilter', canSave)}>
          <ControlLabel>User Search Filter</ControlLabel>
          <FormControl
            type="text"
            value={ldapConfig?.searchFilter || ''}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={updateLDAP('searchFilter') as FormEventHandler<any>}
            placeholder="(uid={{username}})"
          />
          <HelpBlock>
            The filter to use when searching for users. Must include <code>{'{{username}}'}</code>, which will be
            replaced with the username or email used to login.
          </HelpBlock>
        </FormGroup>
        <FormGroup validationState={ldapConfig ? validationState(ldapConfig, 'userEmailAttr', canSave) : null}>
          <ControlLabel>User Email attribute</ControlLabel>
          <FormControl
            type="text"
            value={ldapConfig?.userEmailAttr || ''}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={updateLDAP('userEmailAttr') as FormEventHandler<any>}
            placeholder="mail"
          />
          <HelpBlock>The attribute field to use when loading the user’s email</HelpBlock>
        </FormGroup>
        {window.on_prem && (
          <>
            <FormGroup validationState={validationState(ldapConfig, 'allowEmptyEmail', canSave)}>
              <Flex justify="flex-start">
                <ControlLabel className="nomargin">Allow users with no email address</ControlLabel>
                <Checkbox checked={!!ldapConfig?.allowEmptyEmail} onChange={() => toggleCheckbox('allowEmptyEmail')} />
              </Flex>
              <HelpBlock>
                FOSSA normally uses the email address as the username and requires usernames to be globally unique. When
                this option is enabled, you’ll need to select another attribute from your LDAP schema that will be used
                for the username.
              </HelpBlock>
            </FormGroup>
            <FormGroup validationState={validationState(ldapConfig, 'uidAttr', canSave)}>
              <ControlLabel>Username attribute</ControlLabel>
              <FormControl
                type="text"
                value={ldapConfig?.uidAttr || ''}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={updateLDAP('uidAttr') as FormEventHandler<any>}
                placeholder="uid"
              />
              <HelpBlock>The attribute field to use when loading the username</HelpBlock>
            </FormGroup>
          </>
        )}
        <FormGroup validationState={validationState(ldapConfig, 'userNameAttr', canSave)}>
          <ControlLabel>Name attribute</ControlLabel>
          <FormControl
            type="text"
            value={ldapConfig?.userNameAttr || ''}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={updateLDAP('userNameAttr') as FormEventHandler<any>}
            placeholder="displayName"
          />
          <HelpBlock>The attribute field to use when loading the user’s full name</HelpBlock>
        </FormGroup>
      </div>
      <Text.H4>Group Schema Settings</Text.H4>
      <p>FOSSA can automatically add your users to teams provided by your LDAP server.</p>
      <div className="gray-rounded-box">
        <FormGroup validationState={validationState(ldapConfig, 'useGroups', canSave)} style={{ marginBottom: 0 }}>
          <ControlLabel>Synchronize teams</ControlLabel>
          <Checkbox checked={!!ldapConfig?.useGroups} onChange={() => toggleCheckbox('useGroups')} />
        </FormGroup>
        <FormGroup validationState={validationState(ldapConfig, 'groupBase', canSave)}>
          <ControlLabel>Group Base Distinguished Name</ControlLabel>
          <FormControl
            type="text"
            value={ldapConfig?.groupBase || ''}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={updateLDAP('groupBase') as FormEventHandler<any>}
            placeholder="dc=fossa,dc=com"
          />
          <HelpBlock>The Distinguished Name to use when running queries against your server to find a group.</HelpBlock>
        </FormGroup>
        <FormGroup validationState={validationState(ldapConfig, 'groupNameAttr', canSave)}>
          <ControlLabel>Group Name attribute</ControlLabel>
          <FormControl
            type="text"
            value={ldapConfig?.groupNameAttr || ''}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={updateLDAP('groupNameAttr') as FormEventHandler<any>}
            placeholder="cn"
          />
          <HelpBlock>The attribute field on the group which defines its name.</HelpBlock>
        </FormGroup>
        <FormGroup validationState={validationState(ldapConfig, 'groupDNAttr', canSave)}>
          <ControlLabel>Group DN attribute</ControlLabel>
          <FormControl
            type="text"
            value={ldapConfig?.groupDNAttr || ''}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={updateLDAP('groupDNAttr') as FormEventHandler<any>}
            placeholder="dn"
          />
          <HelpBlock>
            The attribute from your user to use for the <code>{'{{dn}}'}</code> interpolation in the Group Search
            Filter.
          </HelpBlock>
        </FormGroup>
        <FormGroup validationState={validationState(ldapConfig, 'groupFilter', canSave)}>
          <ControlLabel>Group Search Filter</ControlLabel>
          <FormControl
            type="text"
            value={ldapConfig?.groupFilter || ''}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={updateLDAP('groupFilter') as FormEventHandler<any>}
            placeholder="(&(objectclass=groupOfUniqueNames)(uniqueMember=uid={{dn}}))"
          />
          <HelpBlock>
            The filter to use when searching for users. You can include <code>{'{{username}}'}</code> which will be
            replaced with the user’s username or email used to login, or <code>{'{{dn}}'}</code> which will be replaced
            with the user attribute specified in Group DN attribute.
          </HelpBlock>
        </FormGroup>
      </div>
      {shouldShowTestModal && (
        <TestLDAPModal
          onHideTestModal={onHideTestModal}
          user={user}
          updateTestUser={updateTestUser}
        />
      )}
    </div>
  );
};

export default LDAPAuth;
